import { useMemo } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { Role, Permission, ROLE_LEVELS } from '../types/auth';

/**
 * Hook for checking user permissions and roles.
 *
 * Graceful fallback: if user has no roles (RBAC not yet configured),
 * all checks return true so the app works as before.
 */
export function usePermissions() {
  const { user } = useAuth();

  // Memoize fallback arrays so identity stays stable across renders when
  // the user's roles/permissions don't actually change. Otherwise the
  // `?? []` would allocate fresh arrays each render and invalidate downstream
  // useMemo deps.
  const roles = useMemo(() => user?.roles ?? [], [user?.roles]);
  const permissions = useMemo(() => user?.permissions ?? [], [user?.permissions]);
  const primaryRole = user?.primaryRole ?? null;

  // If no roles assigned, RBAC is not enforced — grant everything
  const rbacActive = roles.length > 0;

  const permissionSet = useMemo(
    () => new Set(permissions),
    [permissions]
  );

  const hasPermission = (permission: Permission | string): boolean => {
    if (!rbacActive) return true;
    return permissionSet.has(permission);
  };

  const hasAnyPermission = (...perms: (Permission | string)[]): boolean => {
    if (!rbacActive) return true;
    return perms.some((p) => permissionSet.has(p));
  };

  const hasAllPermissions = (...perms: (Permission | string)[]): boolean => {
    if (!rbacActive) return true;
    return perms.every((p) => permissionSet.has(p));
  };

  const hasRole = (minRole: Role): boolean => {
    if (!rbacActive) return true;
    if (!primaryRole) return false;
    const minLevel = ROLE_LEVELS[minRole] ?? 0;
    const userLevel = ROLE_LEVELS[primaryRole as Role] ?? 0;
    return userLevel >= minLevel;
  };

  // Convenience helpers
  const isAdmin = hasRole(Role.ADMIN);
  const canManageOffers = hasAnyPermission(Permission.OFFERS_CREATE, Permission.OFFERS_UPDATE);
  const canManageCRM = hasPermission(Permission.CRM_MOVE_CANDIDATE);
  const canViewAdmin = hasPermission(Permission.ADMIN_ES_MANAGEMENT);
  const canExportCandidates = hasPermission(Permission.CANDIDATES_EXPORT);
  const canViewAnalytics = hasAnyPermission(
    Permission.ANALYTICS_CANDIDATES,
    Permission.ANALYTICS_OFFERS
  );

  return {
    roles,
    permissions,
    primaryRole,
    rbacActive,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    canManageOffers,
    canManageCRM,
    canViewAdmin,
    canExportCandidates,
    canViewAnalytics,
  };
}
